/* eslint‑disable max-lines, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';                         // ★ added useRef
import moment from 'moment';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import useFetchUserByEmail from '../../components/UserInfoLoader/LoadUserInfo';
import { format } from 'date-fns';

import {
  getWorkSchedulesByUser,
  getWorkSchedule,
  listUsersByTeamOne,
  GET_ALL_LOCATIONS,
  listScheduleTemplates,
  ListWorkSchedulesByDateRange,
} from '../../graphql/queries';

import {
  createWorkSchedule,
  updateWorkSchedule,
  deleteWorkSchedule,
} from '../../graphql/mutations';

import ShiftModal from './components/ShiftModal';
import TemplateModal from './components/TemplateModal';
import NotificationSchedule from './components/NotificationSchedule';
import LocationModal from '../../components/Locations/ListLocations';
import TimesheetModal from './components/TimesheetModal';
import CustomEventWrapper from './components/CustomEventWrapper';
import TimeSlotWrapperWithContextMenu from './components/TimeSlotWrapperWithContextMenu';
import LocationReport from './components/LocationReport';
import EventInfoView from './components/EventInfoView';
import LoadingSpinner from '../../components/layout/LoadingSpinner';
import { capitalize } from '../../components/common/capitalize';
import EmptySlotContextMenu from './components/EmptySlotContextMenu';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './WorkSchedules.css';
import { FaPaperPlane } from 'react-icons/fa';

// ---------------------------------------------------------------------------
// Locale – start weeks on Saturday
// ---------------------------------------------------------------------------
moment.updateLocale('en', { week: { dow: 6, doy: 12 } });
const localizer = momentLocalizer(moment);
const DnDCalendar = withDragAndDrop(Calendar);

const WorkSchedules = () => {
  // -------------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------------
  const [events, setEvents] = useState([]);
  const [resources, setResources] = useState([]);
  const [view, setView] = useState(Views.DAY);
  const [currentDisplayedDate, setCurrentDisplayedDate] = useState(moment().toDate());

  // … modal and UI state (unchanged) …
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isNotificationScheduleOpen, setIsNotificationScheduleOpen] = useState(false);
  const [isTimesheetModalOpen, setIsTimesheetModalOpen] = useState(false);
  const [isLocationReportOpen, setIsLocationReportOpen] = useState(false);
  const [isEventInfoOpen, setIsEventInfoOpen] = useState(false);
  const [selectedEventInfo, setSelectedEventInfo] = useState(null);

  const [selectedLocation, setSelectedLocation] = useState('');
  const [locations, setLocations] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [navigators, setNavigators] = useState([]);
  const [currentUser, setCurrentUser] = useState('');
  const [userGroups, setUserGroups] = useState([]);
  const [userEmail, setUserEmail] = useState(null);
  const [totalHoursPerUserPerDay, setTotalHoursPerUserPerDay] = useState({});
  const [totalHoursPerUserPerWeek, setTotalHoursPerUserPerWeek] = useState({});
  const [currentWeek, setCurrentWeek] = useState(moment().startOf('week'));
  const [hasCopiedShift, setHasCopiedShift] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  // -------------------------------------------------------------------------
  // NEW – cache of IDs already rendered to avoid duplicates
  // -------------------------------------------------------------------------
  const seenIdsRef = useRef(new Set());                                           // ★
  const addEventsUnique = (newEvents) => {                                       // ★
    setEvents(prev => {
      const uniq = [];
      const seen = seenIdsRef.current;
      for (const evt of newEvents) {
        if (!seen.has(evt.id)) { seen.add(evt.id); uniq.push(evt); }
      }
      return uniq.length ? [...prev, ...uniq] : prev;        // no useless re‑render
    });
  };

  // -------------------------------------------------------------------------
  // GRAPHQL hooks  (fetchPolicy 'network-only' to skip cache duplicates) ★
  // -------------------------------------------------------------------------
  const [fetchUserSchedules] = useLazyQuery(getWorkSchedulesByUser, { fetchPolicy: 'network-only' }); // ★
  const [fetchSchedule] = useLazyQuery(getWorkSchedule);
  const [fetchUsers] = useLazyQuery(listUsersByTeamOne);
  const [fetchTemplates] = useLazyQuery(listScheduleTemplates);
  const [saveWorkSchedule] = useMutation(createWorkSchedule);
  const [updateSchedule] = useMutation(updateWorkSchedule);
  const [removeWorkSchedule] = useMutation(deleteWorkSchedule);
  const [fetchWorkSchedulesByDateRange] = useLazyQuery(ListWorkSchedulesByDateRange);

  // -------------------------------------------------------------------------
  // Authentication & user details (unchanged)
  // -------------------------------------------------------------------------
  const fetchUserDetails = async () => {
    try {
      const { signInDetails } = await getCurrentUser();
      const session = await fetchAuthSession();
      const groups = session.tokens.idToken.payload['cognito:groups'] || [];
      setUserGroups(groups);
      setUserEmail(signInDetails.loginId.toLowerCase());
    } catch (err) { console.log('User not authenticated', err); }
  };
  const { userInfo: userLoggedIn } = useFetchUserByEmail(userEmail);
  const isAdmin = userGroups?.some(g => g === 'Admin' || g === 'Manager');

  // -------------------------------------------------------------------------
  // Locations query (unchanged)
  // -------------------------------------------------------------------------
  const [fetchLocations] = useLazyQuery(GET_ALL_LOCATIONS, {
    fetchPolicy: 'network-only',
    onCompleted: data => {
      const mapped = data?.listLocationIndependents?.items?.map(i => ({
        id: i.id, name: i.name, color: i.color || 'rgba(169,169,169,0.5)', addres: i.address
      }))?.sort((a, b) => a.name.localeCompare(b.name)) || [];
      setLocations(mapped);
    },
    onError: err => console.error('Error fetching locations:', err),
  });

  // -------------------------------------------------------------------------
  // Templates loader (unchanged)
  // -------------------------------------------------------------------------
  const loadAllScheduleTemplates = async () => {
    setIsLoading(true);
    try {
      const { data } = await fetchTemplates({ variables: { limit: 100 } });
      const mapped = data?.listScheduleTemplates?.items?.map(t => ({
        id: t.id, name: t.name, days: t.days.map(d => ({ day: d.day, shift: d.shift })), totalHours: t.totalHours
      })) || [];
      setTemplates(mapped);
    } catch (err) { console.error('Error loading templates:', err); }
    finally { setIsLoading(false); }
  };

  // -------------------------------------------------------------------------
  // Load all users then their schedules (unchanged except dedup helper) ★
  // -------------------------------------------------------------------------
  const loadAllUsersAndSchedules = async () => {
    try {
      const { data } = await fetchUsers({ variables: { limit: 50 } });

      const users = data?.listUserFixes?.items
        ?.filter(u => u.first_name && u.last_name)
        .map(u => ({
          id: u.id,
          title: `${capitalize(u.first_name)} ${capitalize(u.last_name)}`,
        }))
        .sort((a, b) => a.title.localeCompare(b.title)) || [];

      setResources(users);
      setNavigators(users);

      // 1️⃣ pull the first page for every user in parallel
      const firstPages = await Promise.all(users.map(fetchFirstPage));

      // 2️⃣ show those events immediately
      addEventsUnique(firstPages.flatMap(p => p.events));

      // 3️⃣ hide the spinner – UI is now responsive
      setIsLoading(false);

      // 4️⃣ fetch the rest in the background
      firstPages.forEach((page, idx) => {
        if (page.nextToken) fetchRemainingPages(users[idx], page.nextToken);
      });
    } catch (err) {
      console.error('Error loading users & schedules:', err);
    }
  };


  // -------------------------------------------------------------------------
  // Initial load (unchanged)
  // -------------------------------------------------------------------------
  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        await fetchLocations();
        await loadAllUsersAndSchedules();
        await loadAllScheduleTemplates();
      } finally { setIsLoading(false); }
    })();
  }, []);

  useEffect(() => { fetchUserDetails(); }, []);

  // -------------------------------------------------------------------------
  // DRAG, RESIZE, DELETE handlers (unchanged)
  // -------------------------------------------------------------------------
  const handleEventDrop = async ({ event, start, end, allDay }) => {
    const date = moment(start).format('YYYY-MM-DD');
    const startTime = moment(start).format('HH:mm');
    const endTime = moment(end).format('HH:mm');

    if (event.isTemporary) {
      // Just update local state, do not call DB
      const updatedTempEvent = { ...event, start, end, allDay };
      setEvents((prev) => prev.map((evt) => (evt.id === event.id ? updatedTempEvent : evt)));
      return;
    }

    setIsLoading(true);
    try {
      if (event.id) {
        const updateInput = { id: event.id, date, startTime, endTime };
        await updateSchedule({ variables: { input: updateInput } });
      }
      // Update local
      const updatedEvent = { ...event, start, end, allDay };
      setEvents((prev) => prev.map((evt) => (evt.id === event.id ? updatedEvent : evt)));
    } catch (error) {
      console.error('Error updating on drop:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEventResize = async ({ event, start, end, allDay }) => {
    const date = moment(start).format('YYYY-MM-DD');
    const startTime = moment(start).format('HH:mm');
    const endTime = moment(end).format('HH:mm');

    if (event.isTemporary) {
      // Just update local state
      const updatedTempEvent = { ...event, start, end, allDay };
      setEvents((prev) => prev.map((evt) => (evt.id === event.id ? updatedTempEvent : evt)));
      return;
    }

    setIsLoading(true);
    try {
      if (event.id) {
        const updateInput = { id: event.id, date, startTime, endTime };
        await updateSchedule({ variables: { input: updateInput } });
      }
      // Update local
      const updatedEvent = { ...event, start, end, allDay };
      setEvents((prev) => prev.map((evt) => (evt.id === event.id ? updatedEvent : evt)));
    } catch (error) {
      console.error('Error updating on resize:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteShift = async () => {
    if (!selectedShift?.id) return;

    // If it's a temporary event (just remove from UI)
    if (selectedShift.isTemporary) {
      setEvents((prev) => prev.filter((evt) => evt.id !== selectedShift.id));
      setIsModalOpen(false);
      setSelectedShift(null);
      setSelectedSlot(null);
      return;
    }

    setIsLoading(true);
    try {
      // DB deletion
      await removeWorkSchedule({ variables: { input: { id: selectedShift.id } } });

      // Immediately remove from state
      setEvents((prev) => prev.filter((evt) => evt.id !== selectedShift.id));

      // Optionally refetch if you want everything fresh:
      // await refetchAllSchedules();

      // Close modal
      setIsModalOpen(false);
      setSelectedShift(null);
      setSelectedSlot(null);


    } catch (err) {
      console.error('Error deleting shift:', err);
      alert('Failed to delete shift. Check console for details.');
    } finally {
      setIsLoading(false);
    }
  };



  // -------------------------------------------------------------------------
  // Save (create/update) shift – uses addEventsUnique for temp/create ★
  // -------------------------------------------------------------------------
  const handleSaveShift = async (shift) => {
    const foundLocation = locations.find(l => l.name === shift.location);
    const isTemp = !foundLocation;
    const isRealId = typeof shift.id === 'string' && shift.id.length > 10 && !shift.id.startsWith('temp-id');

    const input = {
      date: shift.date, startTime: shift.startTime, endTime: shift.endTime,
      breakStart: shift.breakStart || '00:00', breakEnd: shift.breakEnd || '00:00',
      location: shift.location, userId: shift.resourceId,
    };

    setIsLoading(true);
    try {
      if (isRealId && !shift.isTemporary) {
        await updateSchedule({ variables: { input: { id: shift.id, ...input } } });
        setEvents(prev => prev.map(evt => evt.id === shift.id
          ? {
            ...evt, start: new Date(`${input.date}T${input.startTime}`),
            end: new Date(`${input.date}T${input.endTime}`),
            location: input.location
          }
          : evt));
      } else {
        let newId = shift.id;
        if (!isTemp) {
          const { data } = await saveWorkSchedule({ variables: { input } });
          newId = data?.createWorkSchedule?.id || newId;
        } else newId = `temp-id-${Date.now()}`;

        addEventsUnique([{
          id: newId,
          title: `${findUserName(shift.resourceId)} — ${shift.location} Shift`,
          start: new Date(`${input.date}T${input.startTime}`),
          end: new Date(`${input.date}T${input.endTime}`),
          resourceId: shift.resourceId,
          location: shift.location,
          allDay: false,
          isTemporary: isTemp,
        }]);
      }
    } catch (err) { console.error('Error saving shift:', err); }
    finally { setIsLoading(false); setIsModalOpen(false); setSelectedSlot(null); setSelectedShift(null); }
  };

  // -------------------------------------------------------------------------
  // onSaveTemplateShifts – final add uses dedup helper ★
  // -------------------------------------------------------------------------
  const dayOffsetMap = { Saturday: 0, Sunday: 1, Monday: 2, Tuesday: 3, Wednesday: 4, Thursday: 5, Friday: 6 };
  async function onSaveTemplateShifts(navigatorId, startDate, template, location) {
    if (!navigatorId || !startDate || !template?.days) { alert('Required fields missing'); return; }
    setIsLoading(true);
    try {
      const selectedDate = moment(startDate, 'YYYY-MM-DD');
      const diffToSat = ((selectedDate.day() + 7) - 6) % 7;
      const baseSaturday = selectedDate.clone().subtract(diffToSat, 'days');
      const newShifts = [];
      for (const d of template.days) {
        if (!d.shift || d.shift.toUpperCase() === 'OFF') continue;
        const offset = dayOffsetMap[d.day]; if (offset === undefined) continue;
        const [startTime, endTime] = d.shift.split('-');
        if (!startTime || !endTime) continue;

        const dateStr = baseSaturday.clone().add(offset, 'days').format('YYYY-MM-DD');
        const { data } = await saveWorkSchedule({
          variables: {
            input: {
              userId: navigatorId, date: dateStr, startTime, endTime,
              breakStart: '12:00', breakEnd: '13:00', location
            }
          }
        });
        const createdId = data?.createWorkSchedule?.id;
        newShifts.push({
          id: createdId,
          title: `${findUserName(navigatorId)} — ${location} Shift`,
          start: new Date(`${dateStr}T${startTime}`),
          end: new Date(`${dateStr}T${endTime}`),
          resourceId: navigatorId,
          location,
          breakStart: '12:00',
          breakEnd: '13:00',
          allDay: false,
          isTemporary: false,
        });
      }
      addEventsUnique(newShifts);                                               // ★
    } catch (e) { console.error('Error template shifts:', e); }
    finally { setIsLoading(false); setIsModalOpen(false); }
  }

  // -------------------------------------------------------------------------
  // Helper findUserName (unchanged)
  // -------------------------------------------------------------------------
  const findUserName = id => resources.find(u => u.id === id)?.title || 'Unknown';

  // -------------------------------------------------------------------------
  // Copy‑/Paste‑Shift (only call handleSaveShift; dedup handled there)
  // -------------------------------------------------------------------------
  const handleSelectSlot = (slotInfo) => {
    if (!isAdmin) return;
    const copied = localStorage.getItem('copiedShift');
    setHasCopiedShift(true);
    const isCopied = !!copied && (() => { try { const p = JSON.parse(copied); return p?.start && p?.end; } catch { return false; } })();
    if (isCopied) {
      const parsed = JSON.parse(copied);
      const originalStart = new Date(parsed.start);
      const originalEnd = new Date(parsed.end);
      const clickedDate = new Date(slotInfo.start);
      const newStart = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate(),
        originalStart.getHours(), originalStart.getMinutes());
      const newEnd = new Date(clickedDate.getFullYear(), clickedDate.getMonth(), clickedDate.getDate(),
        originalEnd.getHours(), originalEnd.getMinutes());
      const res = resources.find(r => r.id === slotInfo.resourceId);
      const pasted = {
        ...parsed,
        id: Date.now(),
        title: `${res ? res.title : parsed.title} — ${parsed.location || ''} Shift`,
        start: newStart, end: newEnd, resourceId: slotInfo.resourceId || '',
      };
      const date = moment(newStart).format('YYYY-MM-DD');
      handleSaveShift({
        ...pasted, date, startTime: moment(newStart).format('HH:mm'),
        endTime: moment(newEnd).format('HH:mm'), isFromPaste: true
      });
      return;
    }
    // default behaviour – open new‑shift modal
    const date = moment(slotInfo.start).format('YYYY-MM-DD');
    setSelectedSlot({
      date,
      startTime: moment(slotInfo.start).format('HH:mm'),
      endTime: moment(slotInfo.end).format('HH:mm'),
      resourceId: slotInfo.resourceId || '',
      location: '',
      isTemporary: false,
    });
    setSelectedShift(null);
    setIsModalOpen(true);
  };

  const handleSelectEvent = async (event) => {
    if (!isAdmin) {
      setSelectedEventInfo(event);
      setIsEventInfoOpen(true);
      return; // ✅ Prevent execution of admin-only logic below
    }
    else {
      setSelectedEventInfo(event);

    }

    // If it's temporary, no DB fetch needed
    if (event.isTemporary || String(event.id).startsWith('temp-id')) {
      setSelectedShift({
        ...event,
        date: moment(event.start).format('YYYY-MM-DD'),
        startTime: moment(event.start).format('HH:mm'),
        endTime: moment(event.end).format('HH:mm'),
        breakStart: '',
        breakEnd: '',
      });
      setSelectedSlot(null);
      setIsModalOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const schedule = await fetchSchedule({ variables: { id: event.id } });
      const dbItem = schedule?.data?.getWorkSchedule;

      if (dbItem) {
        const { id, userId, date, startTime, endTime, breakStart, breakEnd, location } = dbItem;
        setSelectedShift({
          id,
          resourceId: userId,
          date,
          startTime,
          endTime,
          breakStart,
          breakEnd,
          location,
          title: `${findUserName(userId)} — ${location} Shift`,
          isTemporary: false,
        });
      } else {
        setSelectedShift({
          ...event,
          date: moment(event.start).format('YYYY-MM-DD'),
          startTime: moment(event.start).format('HH:mm'),
          endTime: moment(event.end).format('HH:mm'),
          breakStart: event.breakStart || '',
          breakEnd: event.breakEnd || '',
          location: event.location || '',
          isTemporary: true,
        });
      }

      setSelectedSlot(null);
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching shift details:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // ---------------------------------------
  // DELETE via 'Delete' Key
  // ---------------------------------------
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isAdmin) return; // ❌ Non-admins cannot delete shifts
      // If we have an event selected and the user presses 'Delete':
      if (e.key === 'Delete' && selectedShift) {
        e.preventDefault();
        handleDeleteShift();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedShift, isAdmin]); // <-- Ensure it reacts to isAdmin changes

  // ---------------------------------------
  // EVENT COLORING by Location or Temporary
  // ---------------------------------------
  const getTextColor = (bgColor) => {
    const [r, g, b] = bgColor.match(/\d+/g)?.map(Number) || [169, 169, 169];
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000' : '#fff';
  };

  const eventPropGetter = (event) => {
    if (event.isTemporary) {
      return {
        style: {
          backgroundColor: 'rgba(104, 180, 235, 0.5)',
          color: '#fff',
          borderRadius: '4px',
          border: 'none',
        },
      };
    }
    const foundLocation = locations.find((loc) => loc.name === event.location);
    const backgroundColor = foundLocation?.color || 'rgba(169,169,169,0.5)';
    const textColor = getTextColor(backgroundColor);
    return {
      style: {
        backgroundColor,
        color: textColor,
        borderRadius: '4px',
        border: 'none',
      },
    };
  };

  // ---------------------------------------
  // Filter events by selected user + location
  // ---------------------------------------
  const filteredEvents = React.useMemo(() => {
    return events.filter((evt) => {
      const matchesUser = currentUser ? evt.resourceId === currentUser : true;
      const matchesLocation = selectedLocation ? evt.location === selectedLocation : true;
      return matchesUser && matchesLocation;
    });
  }, [events, currentUser, selectedLocation]);

  // When the user changes in the select:
  const handleUserChange = (e) => {
    const value = e.target.value;
    setCurrentUser(value);
    if (value) setView(Views.WEEK);
    else setView(Views.DAY);
  };
  // ---------------------------------------
  // Calculate total hours per day
  // ---------------------------------------
  const calculateTotalHours = (events) => {
    let dailyHours = {};  // { userId: { "YYYY-MM-DD": hours } }
    let weeklyHours = {}; // { userId: totalHours }

    events.forEach(event => {
      if (event.location === "Lunch Break") {
        return; // Skip this iteration if the event is a lunch break
      }
      const userId = event.resourceId;
      const date = moment(event.start).format('YYYY-MM-DD');
      const start = moment(event.start);
      const end = moment(event.end);
      const duration = moment.duration(end.diff(start)).asHours();

      if (!dailyHours[userId]) dailyHours[userId] = {};
      if (!dailyHours[userId][date]) dailyHours[userId][date] = 0;
      dailyHours[userId][date] += duration;

      if (!weeklyHours[userId]) weeklyHours[userId] = 0;
      weeklyHours[userId] += duration;
    });

    setTotalHoursPerUserPerDay(dailyHours);
    setTotalHoursPerUserPerWeek(weeklyHours);
  };

  // Recalculate whenever events change
  useEffect(() => {
    calculateTotalHours(filteredEvents);
  }, [filteredEvents]);

  useEffect(() => {
    const copied = localStorage.getItem('copiedShift');
    setHasCopiedShift(!!copied);
  }, []);

  // ---------------------------------------
  // Customize rbc-header of calendar
  // ---------------------------------------
  const CustomResourceHeader = ({ label, resource }) => {
    const today = moment(currentDisplayedDate).format('YYYY-MM-DD')
    const userHours = totalHoursPerUserPerDay[resource.id]?.[today] || 0;
    return (
      <div className="custom-resource-header">
        <span className="navigator-name">{label}</span>
        <span className="navigator-hours">{userHours.toFixed(1)} hrs</span>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  //  util – Dynamo item ➜ react‑big‑calendar event
  // ---------------------------------------------------------------------------
  const mapScheduleToEvent = (s, user) => ({
    id: s.id,
    title: `${user.title} — ${s.location} Shift`,
    start: new Date(`${s.date}T${s.startTime}`),
    end: new Date(`${s.date}T${s.endTime}`),
    allDay: false,
    resourceId: user.id,
    location: s.location,
    isTemporary: false,
  });

  // ---------------------------------------------------------------------------
  //  fetch ONLY the first page for a user (fast initial render)
  // ---------------------------------------------------------------------------
  const fetchFirstPage = async (user) => {
    const { data } = await fetchWorkSchedulesByDateRange({
      variables: {
        startDate: '2025-08-27',
        endDate: '2050-08-29',
        nextToken: null,
      },
    });

    const page = data?.listWorkSchedules ?? {};
    const events = (page.items || [])
      .filter(i => i.userId === user.id) // ✅ manually filter by userId
      .map(i => mapScheduleToEvent(i, user));

    return {
      events,
      nextToken: page.nextToken ?? null,
    };
  };

  // ---------------------------------------------------------------------------
  //  background paginator – pulls the remaining pages after UI is shown
  // ---------------------------------------------------------------------------
  const fetchRemainingPages = async (user, token) => {
    let nextToken = token;
    const seenToken = new Set([token]);

    while (nextToken) {
      const { data } = await fetchWorkSchedulesByDateRange({
        variables: {
          startDate: '2025-08-27',
          endDate: '2050-08-29',
          nextToken,
        },
      });

      const page = data?.listWorkSchedules;
      if (!page?.items?.length) break;

      const userEvents = page.items
        .filter(i => i.userId === user.id) // ✅ manually filter by userId
        .map(i => mapScheduleToEvent(i, user));

      addEventsUnique(userEvents);

      nextToken = page.nextToken ?? null;
      if (!nextToken || seenToken.has(nextToken)) break;
      seenToken.add(nextToken);
    }
  };

  return (
    <div className="WorkSchedules">
      <header className="WorkSchedules-header">
        <h1>Work Schedule</h1>
      </header>

      {/* If loading, display a full-screen overlay with the spinner */}
      {isLoading && (
        <div className="loading-overlay">
          <LoadingSpinner />
        </div>
      )}

      <div className="ws-toolbar">
        <div className="ws-selectors">
          {/* Location Selector */}
          <div className="ws-selector">
            <label htmlFor="location-selector">Location:</label>
            <select
              id="location-selector"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              disabled={isLoading}
            >
              <option value="">All Locations</option>
              {locations.map((loc) => (
                <option key={loc.id} value={loc.name}>
                  {loc.name}
                </option>
              ))}
            </select>
          </div>

          {/* User Selector */}
          <div className="ws-selector">
            <label htmlFor="user-selector">User:</label>
            <select
              id="user-selector"
              value={currentUser}
              onChange={handleUserChange}
              disabled={isLoading}
            >
              <option value="">All</option>
              {resources.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.title}
                </option>
              ))}
            </select>
          </div>
          {/* Location Modal Button */}
          {isAdmin && (
            <button
              className="ws-location-btn"
              onClick={() => setIsLocationModalOpen(true)}
              disabled={isLoading}
            >
              +/- Location
            </button>
          )}
          {/* Template Modal Button */}
          {isAdmin && (
            <button
              className="ws-template-btn"
              onClick={() => setIsTemplateModalOpen(true)}
              disabled={isLoading}
            >
              Manage Templates
            </button>
          )}
          {/* Notification Button */}
          {isAdmin && (
            <>
              <button
                className="ws-template-btn"
                onClick={() => setIsNotificationScheduleOpen(true)}
                disabled={isLoading}
              > Send Notification Email
                <FaPaperPlane className='fabars-ws-icon' size={20} />
              </button>
            </>
          )}
        </div>
      </div>
      <div className="ws-toolbar">
        <button
          className="ws-see-timesheet-btn"
          onClick={() => setIsTimesheetModalOpen(true)}
          disabled={isLoading}
        >
          Visualize Timesheets
        </button>
        {isAdmin && (
          <button
            className="ws-see-location-report-btn"
            onClick={() => setIsLocationReportOpen(true)}
            disabled={isLoading}
          >
            See Location Report
          </button>
        )}
        {hasCopiedShift && (
          <button
            onClick={() => {
              localStorage.removeItem('copiedShift');
              setHasCopiedShift(false);
              alert("🚫 Copied shift canceled");
            }}
            style={{
              margin: '8px',
              padding: '6px 12px',
              backgroundColor: '#ef4444',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            🧹 Clear Copied Shift
          </button>
        )}

      </div>
      <div className="ws-work-schedules-wrapper">

        <DnDCalendar
          localizer={localizer}
          events={filteredEvents}
          resources={view === Views.DAY ? resources : undefined}
          resourceIdAccessor="id"
          resourceTitleAccessor="title"
          startAccessor="start"
          endAccessor="end"
          defaultDate={new Date()}
          view={view}
          onView={setView}
          views={[Views.DAY, Views.WEEK, Views.MONTH]}
          className="ws-work-schedules-calendar"
          draggableAccessor={() => !isLoading} // disable dragging if loading
          resizableAccessor={() => !isLoading} // disable resizing if loading
          selectable={!isLoading}
          scrollToTime={new Date(1970, 1, 1, 6, 0)} // <--- Focus at 5 AM
          min={new Date(1970, 1, 1, 5, 0)}         // <--- Earliest time shown
          components={{
            resourceHeader: CustomResourceHeader,
            event: CustomEventWrapper,
            timeSlotWrapper: TimeSlotWrapperWithContextMenu,
          }}

          max={new Date(1970, 1, 1, 23, 0)}        // <--- Latest time shown
          style={{ height: '80vh' }}
          formats={{
            dayHeaderFormat: (date, culture, localizer) => {
              const formattedDate = localizer.format(date, 'dddd, MMM D', culture);
              const dateKey = moment(date).format('YYYY-MM-DD');
              return `${formattedDate}`;
            }
          }}
          onNavigate={(date, view, action) => {
            setCurrentWeek(moment(date).startOf('week'));
            setCurrentDisplayedDate(date)
          }}
          onEventDrop={isAdmin ? handleEventDrop : null} // ❌ Non-admins cannot move shifts
          onEventResize={isAdmin ? handleEventResize : null} // ❌ Non-admins cannot resize shifts
          onSelectSlot={isAdmin ? handleSelectSlot : null} // ❌ Non-admins cannot create new shifts
          onSelectEvent={handleSelectEvent} // ❌ Non-admins cannot open shift modal
          eventPropGetter={eventPropGetter}

        />

      </div>

      {/* Shift Modal */}
      <ShiftModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedSlot(null);
          setSelectedShift(null);
        }}
        onSave={handleSaveShift}
        onSaveTemplateShifts={onSaveTemplateShifts}
        shift={selectedShift || selectedSlot}
        locations={locations}
        event={selectedEventInfo}
        navigators={navigators}
        onDelete={handleDeleteShift}
        templates={templates}
      />

      {/* Location Modal */}
      <LocationModal
        isOpen={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
      />

      {/* Template Modal */}
      <TemplateModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        templates={templates}
        refetchTemplates={loadAllScheduleTemplates}
      />

      <TimesheetModal
        isOpen={isTimesheetModalOpen}
        onClose={() => setIsTimesheetModalOpen(false)}
        currentWeek={currentWeek}
        filteredEvents={filteredEvents}
        locations={locations}
        currentUser={currentUser}
        handleUserChange={handleUserChange}
        resources={resources}
        setCurrentWeek={setCurrentWeek}
        selectedLocation={selectedLocation}
        setSelectedLocation={setSelectedLocation}
        isLoading={isLoading}
        eventPropGetter={eventPropGetter}
      />
      <LocationReport
        isOpen={isLocationReportOpen}
        onClose={() => setIsLocationReportOpen(false)}
        events={events}        // all your schedule events
        resources={resources}  // list of users for the dropdown
      />
      <NotificationSchedule
        isOpen={isNotificationScheduleOpen}
        onClose={() => setIsNotificationScheduleOpen(false)}
        idSupervisor={userLoggedIn?.id}
        NameLogin={`${userLoggedIn?.first_name.trim()} ${userLoggedIn?.last_name.trim()}`}
      />
      <EventInfoView
        isOpen={isEventInfoOpen}
        onClose={() => setIsEventInfoOpen(false)}
        event={selectedEventInfo}
        locations={locations}
      />
    </div>
  );
};

export default WorkSchedules;
